import React, { useEffect, useState, useCallback, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import Strike from "@tiptap/extension-strike";
import Link from "@tiptap/extension-link";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import "./CanvasEditor.css";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Quote,
  RotateCcw,
  RotateCw,
  Underline as UnderlineIcon,
  Strikethrough,
  Link as LinkIcon,
  Type,
  MessageSquare,
  Wand2,
} from "lucide-react";
import { CanvasEditorProps } from "./types";
import { useSelector } from "react-redux";
import { selectUser } from "../../redux/features/appData";
import { parseMarkdown } from "../../utils/markdown";

// Custom extension to highlight text in square brackets (for template variables)
const squareBracketHighlightKey = new PluginKey("square-bracket-highlight");

const SquareBracketHighlight = Extension.create({
  name: "squareBracketHighlight",

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: squareBracketHighlightKey,
        props: {
          decorations(state) {
            const decorations: Decoration[] = [];
            const regex = /\[(.*?)\]/g;

            state.doc.descendants((node, pos) => {
              if (node.isText) {
                const text = node.text || "";
                let match;

                while ((match = regex.exec(text)) !== null) {
                  const start = pos + match.index;
                  const end = start + match[0].length;

                  decorations.push(
                    Decoration.inline(start, end, {
                      style:
                        "color: #007bff; background-color: rgba(0, 123, 255, 0.1); padding: 0 4px; border-radius: 3px;",
                    })
                  );
                }
              }
            });

            return DecorationSet.create(state.doc, decorations);
          },
        },
      }),
    ];
  },
});

const isLikelyHtml = (value: string): boolean => {
  if (!value) return false;
  return /<[a-z][\s\S]*>/i.test(value.trim());
};

const toEditorHtml = (value: string): string => {
  if (!value) {
    return "";
  }

  return isLikelyHtml(value) ? value : parseMarkdown(value) || "";
};

export const CanvasEditor: React.FC<CanvasEditorProps> = ({
  content,
  title,
  isLoading = false,
  onChange,
  onTitleChange,
  onPromptUpdate,
  hasUnsavedChanges = false,
}) => {
  const [linkUrl, setLinkUrl] = useState<string>("");
  const [showLinkInput, setShowLinkInput] = useState<boolean>(false);
  const [showPromptInput, setShowPromptInput] = useState<boolean>(false);
  const [promptText, setPromptText] = useState<string>("");
  const linkInputRef = useRef<HTMLInputElement>(null);
  const promptInputRef = useRef<HTMLTextAreaElement>(null);
  const user = useSelector(selectUser);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: false,
        orderedList: false,
        listItem: false,
      }),
      BulletList.configure({
        HTMLAttributes: {
          class: "canvas-editor-bullet-list",
        },
      }),
      OrderedList.configure({
        HTMLAttributes: {
          class: "canvas-editor-ordered-list",
        },
      }),
      ListItem,
      Placeholder.configure({
        placeholder: "Start writing your document here...",
      }),
      Underline,
      Strike,
      Link.configure({
        openOnClick: true,
        linkOnPaste: true,
      }),
      SquareBracketHighlight,
    ],
    content: toEditorHtml(content),
    editable: true,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    autofocus: true,
  });

  // Create a helper function to ensure the editor is focused before running commands
  const focusEditor = useCallback(() => {
    if (editor && !editor.isFocused) {
      editor.commands.focus();
    }
  }, [editor]);

  // Update content when props change
  useEffect(() => {
    if (!editor) {
      return;
    }

    const nextHtml = toEditorHtml(content);

    if (!nextHtml && editor.getHTML() !== "") {
      editor.commands.clearContent(true);
      return;
    }

    if (nextHtml && editor.getHTML() !== nextHtml) {
      editor.commands.setContent(nextHtml, false);
    }
  }, [editor, content]);

  // Focus the link input when it appears
  useEffect(() => {
    if (showLinkInput && linkInputRef.current) {
      setTimeout(() => {
        linkInputRef.current?.focus();
      }, 10);
    }
  }, [showLinkInput]);

  // Focus the prompt input when it appears
  useEffect(() => {
    if (showPromptInput && promptInputRef.current) {
      setTimeout(() => {
        promptInputRef.current?.focus();
      }, 10);
    }
  }, [showPromptInput]);

  const setLink = () => {
    if (!editor) return;

    if (linkUrl === "") {
      editor.chain().extendMarkRange("link").unsetLink().run();
      return;
    }

    // Add https:// if no protocol is specified
    const url =
      linkUrl.startsWith("http://") || linkUrl.startsWith("https://")
        ? linkUrl
        : `https://${linkUrl}`;

    editor.chain().extendMarkRange("link").setLink({ href: url }).run();

    setLinkUrl("");
    setShowLinkInput(false);
    focusEditor();
  };

  const handlePromptSubmit = () => {
    if (promptText.trim() && onPromptUpdate) {
      onPromptUpdate(promptText);
      setPromptText("");
      setShowPromptInput(false);
      focusEditor();
    }
  };

  const CanvasToolbar = () => {
    if (!editor) {
      return null;
    }

    return (
      <div className="canvas-editor-toolbar">
        <div className="canvas-editor-toolbar-section">
          {/* Undo/Redo */}
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().undo().run();
            }}
            disabled={!editor.can().undo()}
            aria-label="Undo"
            title="Undo (Ctrl+Z)">
            <RotateCcw size={16} />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().redo().run();
            }}
            disabled={!editor.can().redo()}
            aria-label="Redo"
            title="Redo (Ctrl+Y)">
            <RotateCw size={16} />
          </button>

          <div className="canvas-editor-toolbar-divider" />

          {/* Text formatting */}
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().toggleBold().run();
            }}
            className={editor.isActive("bold") ? "is-active" : ""}
            aria-label="Bold"
            title="Bold (Ctrl+B)">
            <Bold size={16} />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().toggleItalic().run();
            }}
            className={editor.isActive("italic") ? "is-active" : ""}
            aria-label="Italic"
            title="Italic (Ctrl+I)">
            <Italic size={16} />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().toggleUnderline().run();
            }}
            className={editor.isActive("underline") ? "is-active" : ""}
            aria-label="Underline"
            title="Underline (Ctrl+U)">
            <UnderlineIcon size={16} />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().toggleStrike().run();
            }}
            className={editor.isActive("strike") ? "is-active" : ""}
            aria-label="Strikethrough">
            <Strikethrough size={16} />
          </button>

          <div className="canvas-editor-toolbar-divider" />

          {/* Clear formatting */}
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().unsetAllMarks().clearNodes().run();
            }}
            aria-label="Clear formatting"
            title="Clear formatting">
            <Type size={16} />
          </button>

          <div className="canvas-editor-toolbar-divider" />

          {/* Headings */}
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().toggleHeading({ level: 1 }).run();
            }}
            className={
              editor.isActive("heading", { level: 1 }) ? "is-active" : ""
            }
            aria-label="Heading 1"
            title="Heading 1">
            <Heading1 size={16} />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().toggleHeading({ level: 2 }).run();
            }}
            className={
              editor.isActive("heading", { level: 2 }) ? "is-active" : ""
            }
            aria-label="Heading 2"
            title="Heading 2">
            <Heading2 size={16} />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().toggleHeading({ level: 3 }).run();
            }}
            className={
              editor.isActive("heading", { level: 3 }) ? "is-active" : ""
            }
            aria-label="Heading 3"
            title="Heading 3">
            <Heading3 size={16} />
          </button>

          <div className="canvas-editor-toolbar-divider" />

          {/* Lists */}
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().toggleBulletList().run();
            }}
            className={editor.isActive("bulletList") ? "is-active" : ""}
            aria-label="Bullet List"
            title="Bullet List">
            <List size={16} />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().toggleOrderedList().run();
            }}
            className={editor.isActive("orderedList") ? "is-active" : ""}
            aria-label="Numbered List"
            title="Numbered List">
            <ListOrdered size={16} />
          </button>

          <div className="canvas-editor-toolbar-divider" />

          {/* Blockquote */}
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().toggleBlockquote().run();
            }}
            className={editor.isActive("blockquote") ? "is-active" : ""}
            aria-label="Blockquote"
            title="Blockquote">
            <Quote size={16} />
          </button>

          {/* Link */}
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              if (showLinkInput) {
                setShowLinkInput(false);
                focusEditor();
              } else {
                setShowLinkInput(true);
              }
            }}
            className={editor.isActive("link") ? "is-active" : ""}
            aria-label="Link"
            title="Add Link">
            <LinkIcon size={16} />
          </button>
        </div>

        <div className="canvas-editor-toolbar-section">
          {/* AI Prompt Update */}
          {onPromptUpdate && (
            <button
              className="canvas-editor-ai-prompt-button"
              onMouseDown={(e) => {
                e.preventDefault();
                setShowPromptInput(!showPromptInput);
              }}
              aria-label="Update with AI"
              title="Ask AI to modify this document">
              <Wand2 size={16} />
              <span>Prompt AI</span>
            </button>
          )}
        </div>

        {/* Link input */}
        {showLinkInput && (
          <div className="canvas-editor-inline-input">
            <input
              ref={linkInputRef}
              type="text"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="Enter URL (e.g., example.com)"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setLink();
                } else if (e.key === "Escape") {
                  setShowLinkInput(false);
                  setLinkUrl("");
                  focusEditor();
                }
              }}
            />
            <button
              onMouseDown={(e) => {
                e.preventDefault();
                setLink();
              }}>
              Add Link
            </button>
          </div>
        )}

        {/* AI Prompt input */}
        {showPromptInput && (
          <div className="canvas-editor-inline-input canvas-editor-prompt-input">
            <textarea
              ref={promptInputRef}
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              placeholder="Tell the AI how to modify this document..."
              rows={2}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  handlePromptSubmit();
                } else if (e.key === "Escape") {
                  setShowPromptInput(false);
                  setPromptText("");
                  focusEditor();
                }
              }}
            />
            <div className="canvas-editor-prompt-actions">
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  setShowPromptInput(false);
                  setPromptText("");
                  focusEditor();
                }}>
                Cancel
              </button>
              <button
                className="primary"
                disabled={!promptText.trim()}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handlePromptSubmit();
                }}>
                Update Document
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="canvas-editor">
      {/* Document Title */}
      <div className="canvas-editor-header">
        <input
          type="text"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder="Document Title..."
          className="canvas-editor-title-input"
        />
        <div className="canvas-editor-header-actions">
          {isLoading ? (
            <div className="canvas-editor-status">
              <div className="loading-spinner" />
              <span>Auto-saving...</span>
            </div>
          ) : (
            <div className="canvas-editor-status">
              <span>{hasUnsavedChanges ? "Unsaved changes" : "Saved"}</span>
            </div>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <CanvasToolbar />

      {/* Editor Content */}
      <div className="canvas-editor-content">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};
